// Prim Kademeleri (Tier) — Şirket geneli HEDEF BAZLI prim kademeleri.
// Personel kartındaki commission_type/rate'ten bağımsız çalışır.
// Her kademe: Min Hedef % + Tip (% / TL) + Değer.
//   - 'percent' → bonus = max(0, gerçekleşen - hedef) × değer / 100   (hedef ÜSTÜ ciro üzerinden)
//   - 'fixed'   → bonus = değer  (sabit TL)
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, FormField, ListPageHeader, EmptyState } from '../components/ui';
import { toast } from '../components/Toast';

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
}

export default function CommissionTiersPage() {
  const [cfg, setCfg] = useState({ enabled: false, tiers: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewPct, setPreviewPct] = useState(120);
  const [previewTarget, setPreviewTarget] = useState(1000000);
  const [previewAchieved, setPreviewAchieved] = useState(1200000);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/commissions/tier-config');
      if (r.data.success) {
        const data = r.data.data || { enabled: false, tiers: [] };
        // Eski "multiplier" formatını yeni "type/value" formatına çevir (varsayılan percent)
        if (data.tiers && data.tiers.length > 0 && data.tiers[0].multiplier !== undefined && data.tiers[0].type === undefined) {
          data.tiers = data.tiers.map(t => ({ min_pct: t.min_pct, type: 'percent', value: 0 }));
        }
        setCfg(data);
      }
    } catch (e) { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    if (cfg.tiers.length === 0) { toast.error('En az bir kademe gerekli'); return; }
    const tiers = cfg.tiers
      .map(t => ({ min_pct: parseFloat(t.min_pct), type: (t.type === 'fixed' ? 'fixed' : 'percent'), value: parseFloat(t.value) }))
      .filter(t => !isNaN(t.min_pct) && !isNaN(t.value))
      .sort((a, b) => a.min_pct - b.min_pct);
    setSaving(true);
    try {
      const r = await api.put('/commissions/tier-config', { enabled: cfg.enabled, tiers });
      if (r.data.success) { setCfg(r.data.data); toast.success('Kaydedildi'); }
    } catch (e) { toast.error(e.response?.data?.message || 'Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const addTier = () => setCfg(c => ({ ...c, tiers: [...c.tiers, { min_pct: 100, type: 'percent', value: 1 }] }));
  const removeTier = (i) => setCfg(c => ({ ...c, tiers: c.tiers.filter((_, idx) => idx !== i) }));
  const setTier = (i, field, val) => setCfg(c => ({ ...c, tiers: c.tiers.map((t, idx) => idx === i ? { ...t, [field]: val } : t) }));

  // Önizleme
  const findTier = (pct) => {
    if (!cfg.tiers.length || pct == null) return null;
    const sorted = [...cfg.tiers]
      .map(t => ({ min_pct: parseFloat(t.min_pct), type: t.type, value: parseFloat(t.value) }))
      .sort((a, b) => a.min_pct - b.min_pct);
    let match = null;
    for (const t of sorted) { if (pct >= t.min_pct) match = t; }
    return match;
  };
  const calcBonus = (tier, achieved, target) => {
    if (!tier) return 0;
    if (tier.type === 'fixed') return tier.value || 0;
    if (tier.type === 'percent') {
      const excess = Math.max(0, (achieved || 0) - (target || 0));
      return +(excess * (tier.value || 0) / 100).toFixed(2);
    }
    return 0;
  };
  const previewTier = findTier(parseFloat(previewPct) || 0);
  const previewBonus = calcBonus(previewTier, parseFloat(previewAchieved) || 0, parseFloat(previewTarget) || 0);

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Prim Kademeleri (Hedef Bazlı)"
        subtitle="Şube veya kullanıcı hedef gerçekleşmesine göre prim hesabı. Personel kartından bağımsız çalışır."
        onRefresh={fetch}
        refreshing={loading}
      />

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 1100 }}>
        <div style={{
          padding: 14, borderRadius: 10,
          background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.30)',
          fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6,
        }}>
          📊 <strong>Mantık:</strong> Personel kartında prim ayarı olmasa bile bu sistem çalışır.
          <br/>• <strong>% Tip</strong> → bonus = (gerçekleşen − hedef) × değer / 100. Yani hedef ÜSTÜ ciro üzerinden prim.
          <br/>• <strong>TL Tip</strong> → bonus = sabit tutar (hedef yüzdesi o kademeye gelirse).
          <br/>• <strong>Şube hedefi</strong>: bonus, şubedeki personelin <code>Dağıtım Payı (%)</code>'na göre paylaştırılır.
          <br/>• <strong>Kullanıcı hedefi</strong>: bonus o tek personele yazılır.
        </div>

        <div style={{
          padding: 14, borderRadius: 10,
          background: cfg.enabled ? 'rgba(16,185,129,0.10)' : 'var(--bg-secondary)',
          border: '1px solid ' + (cfg.enabled ? 'rgba(16,185,129,0.40)' : 'var(--border-secondary)'),
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={cfg.enabled} onChange={e => setCfg({ ...cfg, enabled: e.target.checked })}
              style={{ width: 22, height: 22 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                Hedef Prim Sistemi {cfg.enabled ? '✅ Aktif' : '⛔ Pasif'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                Pasifken "Dönem Bonusu Hesapla" boş döner.
              </div>
            </div>
          </label>
        </div>

        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 10, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <strong>📋 Kademeler</strong>
            <span style={{ marginLeft: 'auto' }}>
              <Button size="sm" onClick={addTier}>+ Kademe Ekle</Button>
            </span>
          </div>

          {cfg.tiers.length === 0 ? (
            <EmptyState compact title="Henüz kademe yok" description='"+ Kademe Ekle" ile başlayın.' />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)' }}>
                  <th style={th}>Min Hedef %</th>
                  <th style={th}>Tip</th>
                  <th style={th}>Değer</th>
                  <th style={{ ...th, textAlign: 'left' }}>Anlamı</th>
                  <th style={{ ...th, width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {cfg.tiers.map((t, i) => {
                  const pct = parseFloat(t.min_pct);
                  const val = parseFloat(t.value);
                  const desc = t.type === 'fixed'
                    ? `Hedefin %${pct}'ine ulaşırsa → sabit ${fmt(val)} TL`
                    : `Hedefin %${pct}'ine ulaşırsa → hedef üstü ciro × %${val}`;
                  return (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-secondary)' }}>
                      <td style={td}>
                        <input type="number" step="0.1" value={t.min_pct} onChange={e => setTier(i, 'min_pct', e.target.value)} style={inp} />
                      </td>
                      <td style={td}>
                        <select value={t.type || 'percent'} onChange={e => setTier(i, 'type', e.target.value)} style={{ ...inp, width: 110, textAlign: 'left' }}>
                          <option value="percent">% (yüzde)</option>
                          <option value="fixed">TL (sabit)</option>
                        </select>
                      </td>
                      <td style={td}>
                        <input type="number" step="0.01" value={t.value} onChange={e => setTier(i, 'value', e.target.value)} style={inp}
                          placeholder={t.type === 'fixed' ? '5000' : '1.5'} />
                      </td>
                      <td style={{ ...td, fontSize: 11, color: 'var(--text-tertiary)', textAlign: 'left' }}>
                        {!isNaN(pct) && !isNaN(val) && desc}
                      </td>
                      <td style={td}>
                        <button onClick={() => removeTier(i)} title="Sil" style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 16 }}>🗑</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : '💾 Kaydet'}</Button>
          </div>
        </div>

        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 10, padding: 14 }}>
          <strong style={{ marginBottom: 8, display: 'block' }}>🧪 Önizleme Hesaplayıcısı</strong>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
            <FormField label="Hedef Tutar (TL)">
              <input type="number" value={previewTarget} onChange={e => { setPreviewTarget(e.target.value); const a = parseFloat(previewAchieved) || 0; const t = parseFloat(e.target.value) || 0; setPreviewPct(t > 0 ? ((a / t) * 100).toFixed(1) : 0); }} />
            </FormField>
            <FormField label="Gerçekleşen Tutar (TL)">
              <input type="number" value={previewAchieved} onChange={e => { setPreviewAchieved(e.target.value); const a = parseFloat(e.target.value) || 0; const t = parseFloat(previewTarget) || 0; setPreviewPct(t > 0 ? ((a / t) * 100).toFixed(1) : 0); }} />
            </FormField>
            <FormField label="Hedef Yüzdesi (%)">
              <input type="number" value={previewPct} onChange={e => setPreviewPct(e.target.value)} />
            </FormField>
          </div>
          <div style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--bg-secondary)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>Sonuç</div>
            {previewTier ? (
              <>
                <div style={{ fontSize: 13, marginBottom: 4 }}>
                  Kademe: <strong>%{previewTier.min_pct}+</strong> · {previewTier.type === 'fixed' ? 'Sabit' : 'Yüzde'} · <strong>{previewTier.type === 'fixed' ? fmt(previewTier.value) + ' TL' : '%' + previewTier.value}</strong>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: previewBonus > 0 ? 'var(--success)' : 'var(--text-tertiary)', fontFamily: 'JetBrains Mono, monospace' }}>
                  Bonus: {fmt(previewBonus)} TL
                </div>
                {previewTier.type === 'percent' && previewBonus === 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                    Hedef üstü ciro = max(0, gerçekleşen − hedef) = 0 olduğu için bonus 0. Yüzde tipindeki kademeler sadece hedef ÜSTÜ gerçekleşmede prim üretir.
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>Bu yüzdeye karşılık gelen tier yok (en düşük min_pct'in altında).</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const th = { padding: '8px 10px', fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'right' };
const td = { padding: '8px 10px', textAlign: 'right' };
const inp = { width: 110, padding: '6px 10px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, border: '1px solid var(--border-input)', borderRadius: 6, background: 'var(--bg-input)', color: 'var(--text-primary)' };
